// src/pages/DietsClinic/index.js
//
// Diyetler — klinik geneli beslenme planları (DietPlan) + planı henüz yazılmamış
// diyet talepleri (hekimin tedavide "Diyet talebi aç" dediği kayıtlar). Düz liste
// (docs/design-principles.md §3). Plan satırı → drawer'da DietEditor; talep
// satırı → drawer'da talep bilgisi + "Plan yaz" (plan tedaviye bağlanır).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Restaurant as RestaurantIcon, HourglassEmpty as HourglassIcon } from "@mui/icons-material";
import DietEditor from "../../components/organisms/PatientWorkspaceTabs/DietEditor";
import ActionButton from "../../components/atoms/ActionButton";
import useAuth from "../../hooks/useAuth";
import useTerms from "../../hooks/useTerms";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useAlert } from "../../context/AlertProvider";
import { useClinicTheme } from "../../theme";
import { COLORS, TYPO } from "../../styles/uiConstants";
import {
  useListClinicDietPlansMutation,
  useCreateDietPlanMutation,
} from "../../store/secretaryApi";
import { DataTableCard } from "../../components/organisms";
import {
  ClinicRecordListLayout,
  RecordEditorDrawer,
  ClickableRow,
  DateCell,
  Dash,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PatientCell,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SORTERS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
  mainText,
  patientNameOf,
  subText,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import { fullDateTime } from "../Secretary/PatientsPage/patientListFormat";

const COLS = 5;

const STATUS = {
  active: { label: "Aktif", tone: "success" },
  completed: { label: "Tamamlandı", tone: "neutral" },
  cancelled: { label: "İptal", tone: "neutral" },
  requested: { label: "Talep Edildi", tone: "warning" },
};

const SEGMENTS = [
  { value: "all", label: "Tümü" },
  { value: "active", label: STATUS.active.label },
  { value: "requested", label: STATUS.requested.label },
  { value: "completed", label: STATUS.completed.label },
];

const SEGMENT_EMPTY = {
  active: "Aktif beslenme planı yok.",
  requested: "Bekleyen diyet talebi yok.",
  completed: "Tamamlanmış plan yok.",
};

// Satır: plan veya talep. `kind` ile ayrılır; ortak alanlar patient/date.
const toRows = (data) => [
  ...(data?.plans || []).map((p) => ({ ...p, kind: "plan", rowKey: `p-${p._id}` })),
  ...(data?.requests || []).map((r) => ({ ...r, kind: "request", status: "requested", rowKey: `r-${r._id}` })),
];

const statusOf = (r) => (r.status === "cancelled" ? "completed" : r.status);
const dateOf = (r) => (r.kind === "plan" ? r.updatedAt || r.createdAt : r.requestedAt);
const foodCounts = (r) => ({
  allowed: (r.allowedList || []).length,
  forbidden: (r.forbiddenList || []).length,
});

const matches = (r, text) =>
  patientNameOf(r.patient).toLocaleLowerCase("tr").includes(text) ||
  String(r.patient?.tcNo || "").includes(text) ||
  String(r.title || "").toLocaleLowerCase("tr").includes(text) ||
  String(r.note || r.dietNote || "").toLocaleLowerCase("tr").includes(text) ||
  String(r.diagnosis || "").toLocaleLowerCase("tr").includes(text);

const SORT_FNS = { name: SORTERS.name((r) => r.patient), date: SORTERS.date(dateOf) };
const DEFAULT_DIR = { name: "asc", date: "desc" };

export default function DietsClinic() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError, showSuccess } = useAlert();
  const canEdit = useHasPermission("diet:update");
  const canCreate = useHasPermission("diet:create");

  const [listPlans, { isLoading }] = useListClinicDietPlansMutation();
  const [createPlan, { isLoading: creating }] = useCreateDietPlanMutation();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const selected = useMemo(() => rows.find((r) => r.rowKey === selectedKey) || null, [rows, selectedKey]);

  const refresh = useCallback(async () => {
    if (!token) return null;
    try {
      const res = await listPlans({ token }).unwrap();
      const next = toRows(res?.data);
      setRows(next);
      return next;
    } catch (_) {
      showError("Diyetler yüklenemedi.");
      return null;
    } finally {
      setLoaded(true);
    }
  }, [token, listPlans, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const list = useClientList(rows, {
    segmentOf: statusOf,
    matches,
    sorters: SORT_FNS,
    defaultDir: DEFAULT_DIR,
    initialSort: "date",
  });

  const segments = useMemo(
    () => SEGMENTS.map((s) => ({ value: s.value, label: `${s.label} · ${list.counts[s.value] || 0}` })),
    [list.counts]
  );

  const handleSaved = useCallback((updated) => {
    if (!updated?._id) return;
    setRows((prev) =>
      prev.map((r) => (r.kind === "plan" && r._id === updated._id ? { ...r, ...updated, patient: r.patient } : r))
    );
  }, []);

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`);
        return;
      }
      showError(serverMsg || "Plan kaydedilemedi.");
    },
    [showError, terms]
  );

  // Talepten plan yaz: plan tedaviye bağlanır, talep kapanır, drawer plana geçer.
  const writePlanForRequest = async (req) => {
    try {
      const res = await createPlan({
        token,
        patientId: req.patient?._id,
        treatmentId: req._id,
        title: req.diagnosis ? `Diyet · ${req.diagnosis}` : "Beslenme planı",
        note: req.dietNote || "",
        days: [{ label: "Her gün", meals: [] }],
      }).unwrap();
      const planId = res?.data?.plan?._id;
      await refresh();
      if (planId) setSelectedKey(`p-${planId}`);
      showSuccess("Plan oluşturuldu; diyet talebi kapandı.");
    } catch (err) {
      showError(err?.data?.message || "Plan oluşturulamadı.");
    }
  };

  const requestedCount = rows.filter((r) => r.kind === "request").length;
  const notice =
    requestedCount > 0 && list.segment !== "requested"
      ? {
          icon: <HourglassIcon />,
          text: (
            <>
              <strong>{requestedCount} diyet talebi</strong> plan yazılmasını bekliyor.
            </>
          ),
          actionLabel: "Görüntüle",
          onClick: () => list.setSegment("requested"),
        }
      : null;

  const busy = isLoading && !loaded;
  const empty = list.searchText
    ? { title: "Diyet bulunamadı", message: `“${list.searchText}” aramanızla eşleşen kayıt yok. Aramayı değiştirin.` }
    : list.segment !== "all"
    ? { title: "Bu filtrede kayıt yok", message: SEGMENT_EMPTY[list.segment] }
    : { title: "Henüz beslenme planı yok", message: `Planlar ${terms.patientLower} detayındaki Beslenme Planı sekmesinden yazılır.` };

  const sortProps = { sort: list.sort, dir: list.dir, onSort: list.onSort };

  return (
    <>
      <ClinicRecordListLayout
        icon={<RestaurantIcon sx={{ fontSize: 22 }} />}
        title="Diyetler"
        subtitle="Beslenme planları ve bekleyen diyet talepleri"
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={`${terms.patient} adı, TC no, plan adı veya not ile ara`}
        segments={segments}
        segment={list.segment}
        onSegmentChange={list.setSegment}
        segmentAriaLabel="Plan durumu"
        notice={notice}
      >
        <DataTableCard
          loading={Boolean(busy)}
          variant="flat"
          empty={!busy && list.filtered.length === 0}
          emptyIcon={<RestaurantIcon />}
          emptyTitle={empty.title}
          emptyMessage={empty.message}
          page={list.page}
          rowsPerPage={list.rowsPerPage}
          totalCount={list.filtered.length}
          onPageChange={list.onPageChange}
          onRowsPerPageChange={list.onRowsPerPageChange}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <SortHead field="name" label={terms.patient} {...sortProps} sx={{ ...EDGE_L, width: "26%" }} />
                <PlainHead sx={{ width: "30%" }}>Plan</PlainHead>
                <PlainHead sx={{ width: "20%" }}>Günlük enerji · besinler</PlainHead>
                <SortHead field="date" label="Tarih" defaultDir="desc" {...sortProps} sx={{ width: "12%" }} />
                <PlainHead sx={{ ...EDGE_R, width: "12%" }}>Durum</PlainHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {busy ? (
                <SkeletonRows rows={Math.min(10, list.rowsPerPage)} cols={COLS} />
              ) : list.pagedRows.length ? (
                list.pagedRows.map((row) => {
                  const st = STATUS[row.status] || STATUS.completed;
                  const isPlan = row.kind === "plan";
                  const { allowed, forbidden } = foodCounts(row);
                  const kcal = isPlan ? row.days?.[0]?.totals?.calories : null;
                  const note = String(isPlan ? row.note || "" : row.dietNote || "").trim();
                  return (
                    <ClickableRow key={row.rowKey} onClick={() => setSelectedKey(row.rowKey)} palette={palette}>
                      <PatientCell patient={row.patient} />
                      <TextCell
                        main={isPlan ? row.title : "Diyet talebi"}
                        sub={note || row.diagnosis || null}
                        tooltip={note.length > 60 ? note : null}
                      />
                      <TableCell sx={cellSx}>
                        {isPlan && (kcal || allowed || forbidden) ? (
                          <>
                            <Typography sx={mainText} noWrap>
                              {kcal ? `${kcal.toLocaleString("tr-TR")} kcal` : "—"}
                            </Typography>
                            <Typography sx={{ ...subText, color: forbidden ? COLORS.errorDark : COLORS.slate500 }} noWrap>
                              {allowed} serbest · {forbidden} yasaklı
                            </Typography>
                          </>
                        ) : (
                          <Dash />
                        )}
                      </TableCell>
                      <DateCell value={dateOf(row)} />
                      <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                        <StatusPill label={st.label} tone={st.tone} palette={palette} />
                      </TableCell>
                    </ClickableRow>
                  );
                })
              ) : (
                <FillerRow cols={COLS} />
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </ClinicRecordListLayout>

      <RecordEditorDrawer
        open={Boolean(selected)}
        onClose={() => setSelectedKey(null)}
        icon={<RestaurantIcon />}
        title={selected ? patientNameOf(selected.patient) : "Diyet"}
        subtitle={
          selected
            ? `${selected.kind === "plan" ? selected.title : "Diyet talebi"} · ${fullDateTime(dateOf(selected))}`
            : ""
        }
        readOnly={
          selected?.kind === "plan"
            ? !canEdit || selected.status !== "active"
            : !canCreate
        }
        patientLink={
          selected?.patient?._id
            ? `/Hastalar/${selected.patient._id}?tab=beslenme&bolum=plan`
            : null
        }
      >
        {selected?.kind === "plan" && (
          <DietEditor
            key={selected._id}
            record={selected}
            patientId={selected.patient?._id}
            token={token}
            canEdit={canEdit && selected.status === "active"}
            onSaved={handleSaved}
            onError={handleEditorError}
          />
        )}
        {selected?.kind === "request" && (
          <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }}>
              {selected.diagnosis ? `Tanı: ${selected.diagnosis}` : `${terms.doctor} bu ${terms.patientLower} için beslenme planı istedi.`}
            </Typography>
            {selected.dietNote ? (
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate600, whiteSpace: "pre-wrap" }}>
                {selected.dietNote}
              </Typography>
            ) : null}
            {canCreate && (
              <Box>
                <ActionButton
                  variant="add"
                  palette={palette}
                  label="Plan yaz"
                  loading={creating}
                  onClick={() => writePlanForRequest(selected)}
                />
              </Box>
            )}
          </Box>
        )}
      </RecordEditorDrawer>
    </>
  );
}
